import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { movies } from "@/mocks/movies";
import { typeMeta } from "@/mocks/filters";
import { usePageMeta } from "@/hooks/usePageMeta";
import MovieCard from "@/components/feature/MovieCard";
import Pagination from "@/components/feature/Pagination";
import FilterPanel, { type FilterState } from "@/pages/category/components/FilterPanel";
import NotFound from "@/pages/NotFound";

const PAGE_SIZE = 6;

const sortOptions = [
  { key: "popular", label: "热门程度" },
  { key: "latest", label: "最新发布" },
  { key: "release", label: "上映时间" },
];

const emptyFilters: FilterState = { genres: [], regions: [], years: [] };

export default function Category() {
  const { type } = useParams<{ type: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const meta = type ? typeMeta[type] : undefined;

  const [filters, setFilters] = useState<FilterState>(emptyFilters);
  const [sort, setSort] = useState("popular");
  const [page, setPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const keyword = searchParams.get("genre") || "";

  usePageMeta({
    title: meta ? meta.title : "秋霞影院 - 分类列表",
    description: meta
      ? meta.description
      : "秋霞影院影视分类列表，支持按类型、地区、年份筛选查找高清影视资源。",
    keywords: "秋霞影院,影视分类,电影,电视剧,影视资源",
  });

  useEffect(() => {
    setFilters(keyword ? { ...emptyFilters, genres: [keyword] } : emptyFilters);
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, keyword]);

  const filtered = useMemo(() => {
    if (!type) return [];
    let list = movies.filter((m) => m.type === type);
    if (filters.genres.length) list = list.filter((m) => m.genre.some((g) => filters.genres.includes(g)));
    if (filters.regions.length) list = list.filter((m) => filters.regions.includes(m.region));
    if (filters.years.length) list = list.filter((m) => filters.years.includes(m.year));

    if (sort === "popular") list = [...list].sort((a, b) => b.popularity - a.popularity);
    else if (sort === "latest")
      list = [...list].sort((a, b) => b.releaseDate.localeCompare(a.releaseDate));
    else list = [...list].sort((a, b) => b.year - a.year || b.rating - a.rating);
    return list;
  }, [type, filters, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageList = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  if (!meta) return <NotFound />;

  const updateFilters = (next: FilterState) => {
    setFilters(next);
    setPage(1);
    if (keyword) setSearchParams({});
  };

  const resetFilters = () => {
    setFilters(emptyFilters);
    setSearchParams({});
    setPage(1);
  };

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-6 md:px-6 md:py-8">
      <nav className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑">
        <Link to="/" className="cursor-pointer transition-colors hover:text-accent-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-800">{meta.label}</span>
      </nav>

      <div className="mt-4 flex flex-wrap items-end justify-between gap-3 border-b border-background-200 pb-5">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">{meta.label}专区</h1>
          <p className="mt-1.5 max-w-2xl text-sm text-foreground-500">{meta.description}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-700 lg:hidden"
          >
            <i className="ri-filter-3-line" /> 筛选
          </button>
          <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2">
            <i className="ri-sort-desc text-foreground-500" />
            <select
              value={sort}
              onChange={(e) => {
                setSort(e.target.value);
                setPage(1);
              }}
              className="cursor-pointer bg-transparent text-sm text-foreground-800 outline-none"
            >
              {sortOptions.map((opt) => (
                <option key={opt.key} value={opt.key}>
                  按{opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="mt-5 grid gap-6 lg:grid-cols-[260px_1fr]">
        <aside className="hidden lg:block">
          <FilterPanel value={filters} onChange={updateFilters} onReset={resetFilters} />
        </aside>

        <div>
          <p className="mb-4 text-sm text-foreground-500">
            共找到 <span className="font-semibold text-accent-600">{filtered.length}</span> 部作品
          </p>

          {pageList.length > 0 ? (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4">
              {pageList.map((movie, i) => (
                <MovieCard key={movie.id} movie={movie} rank={(page - 1) * PAGE_SIZE + i + 1} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-background-300 bg-background-50 py-20 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-2xl text-foreground-400">
                <i className="ri-search-eye-line" />
              </span>
              <p className="mt-4 font-heading text-base font-semibold text-foreground-800">未找到相关影视资源</p>
              <p className="mt-1 text-sm text-foreground-500">试试调整筛选条件，或重置后重新查找</p>
              <button
                type="button"
                onClick={resetFilters}
                className="mt-5 cursor-pointer whitespace-nowrap rounded-md bg-primary-600 px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-700"
              >
                重置筛选
              </button>
            </div>
          )}

          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </div>
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div
            className="absolute inset-0 bg-primary-950/50"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
          <div className="absolute right-0 top-0 h-full w-[88%] max-w-sm overflow-y-auto bg-background-100 p-4">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-heading text-base font-semibold text-foreground-950">筛选</h3>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-300"
                aria-label="关闭"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <FilterPanel value={filters} onChange={updateFilters} onReset={resetFilters} />
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="mt-4 w-full cursor-pointer whitespace-nowrap rounded-md bg-accent-500 py-3 text-sm font-semibold text-white transition-colors hover:bg-accent-600"
            >
              查看 {filtered.length} 部作品
            </button>
          </div>
        </div>
      )}
    </div>
  );
}